import React, { useState, useEffect } from 'react';
import { usePages, usePosts, useCategories } from './hooks/useBlog';
import { Link } from 'react-router-dom';

const BlogDashboard = ({ onSelectItem }) => {
  const { data: pages, isLoading: isLoadingPages, error: errorPages } = usePages();
  const { data: posts, isLoading: isLoadingPosts, error: errorPosts } = usePosts();
  const { data: categories } = useCategories();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('latest');
  const itemsPerPage = 3;

  // SEO Management
  useEffect(() => {
    document.title = "GreenMark Insights | Industrial Safety & Sustainability Blog Singapore";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Explore latest insights on industrial training, WSH safety consultancy, sustainability audits, and ITE-approved courses in Singapore. Stay ahead with GreenMark's technical expertise.");
    }
  }, []);

  if (isLoadingPages || isLoadingPosts) return (
    <div className="pt-32 pb-24 px-8 max-w-screen-2xl mx-auto bg-surface animate-pulse">
      <div className="h-8 bg-zinc-200 rounded w-1/4 mb-10"></div>
      <div className="h-20 bg-zinc-200 rounded w-1/2 mb-20"></div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-8 space-y-20">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="space-y-6">
              <div className="aspect-[16/9] bg-zinc-200 rounded-xl"></div>
              <div className="h-10 bg-zinc-200 rounded w-3/4"></div>
              <div className="h-6 bg-zinc-200 rounded w-full"></div>
            </div>
          ))}
        </div>
        <div className="lg:col-span-4 space-y-16">
          <div className="h-[400px] bg-zinc-200 rounded-xl"></div>
          <div className="h-[200px] bg-zinc-200 rounded-xl"></div>
        </div>
      </div>
    </div>
  );
  if (errorPages || errorPosts) return <div className="pt-40 text-center font-body text-error">Unable to fetch insights. Please check your connection to Singapore servers.</div>;

  const allItems = [...(pages || []), ...(posts || [])].sort((a, b) => {
    if (sortBy === 'latest') {
      return new Date(b.date) - new Date(a.date);
    } else {
      // Mock popularity sort: combining ID and date weight
      return b.id - a.id;
    }
  });

  // Pagination Logic
  const totalPages = Math.ceil(allItems.length / itemsPerPage);
  const paginatedItems = allItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <main className="pt-32 pb-24 px-8 max-w-screen-2xl mx-auto animate-in fade-in duration-1000 bg-surface font-body selection:bg-primary-fixed selection:text-on-primary-fixed text-on-surface">
      {/* Category Header */}
      <header className="mb-20">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-3xl text-on-surface">
            <span className="font-label text-xs tracking-[0.2em] uppercase text-primary font-bold mb-4 block">Singapore / Industrial & Safety Insights</span>
            <h1 className="font-headline text-6xl md:text-7xl font-bold tracking-tighter text-on-surface">Innovation & Compliance</h1>
            <p className="mt-6 text-xl text-on-surface-variant font-body leading-relaxed max-w-3xl text-on-surface-variant/80">
              Your definitive resource for industrial safety standards, ITE-approved training modules, WSH consultancy, and GreenMark environmental excellence in Singapore.
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm font-label tracking-wide text-on-surface-variant">
            <span className="opacity-50 font-bold">Sort by:</span>
            <div className="flex gap-4">
              <button 
                onClick={() => setSortBy('latest')}
                className={`transition-all font-bold ${sortBy === 'latest' ? 'text-on-surface border-b-2 border-primary' : 'text-on-surface-variant opacity-60 hover:opacity-100'}`}
              >
                Latest
              </button>
              <button 
                onClick={() => setSortBy('popular')}
                className={`transition-all font-bold ${sortBy === 'popular' ? 'text-on-surface border-b-2 border-primary' : 'text-on-surface-variant opacity-60 hover:opacity-100'}`}
              >
                Popular
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        {/* Article Area */}
        <div className="lg:col-span-8 space-y-20">
          <div className="space-y-24">
            {paginatedItems.map((item, index) => {
              // Featured (First) Article Style
              if (index === 0 && currentPage === 1) {
                return (
                  <article key={item.id} className="group cursor-pointer" onClick={() => onSelectItem(item)}>
                    <div className="flex flex-col gap-10">
                      <div className="relative overflow-hidden rounded-xl aspect-[16/9] bg-surface-container shadow-sm group-hover:shadow-2xl transition-all duration-700">
                        <img 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" 
                          alt="Featured Industrial Article" 
                          src={item.smartThumbnail} 
                        />
                        <div className="absolute top-4 left-4">
                          <span className="bg-white/90 backdrop-blur px-3 py-1 rounded-full font-label text-[10px] font-bold uppercase tracking-widest text-primary shadow-sm">
                            {item.type}
                          </span>
                        </div>
                      </div>
                      <div className="space-y-6">
                        <div className="flex items-center gap-4 text-xs font-label tracking-widest uppercase text-on-surface-variant">
                          <span className="text-primary font-bold">{new Date(item.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          <span className="w-1 h-1 bg-outline-variant rounded-full"></span>
                          <span>Industry Standard</span>
                        </div>
                        <h2 className="font-headline text-4xl md:text-5xl font-bold group-hover:text-primary transition-colors leading-tight text-on-surface" dangerouslySetInnerHTML={{ __html: item.title.rendered }} />
                        <div className="text-on-surface-variant text-lg leading-relaxed max-w-3xl line-clamp-3 font-body opacity-80" dangerouslySetInnerHTML={{ __html: item.excerpt?.rendered || item.content?.rendered?.substring(0, 200) || "Experience professional safety training and industrial excellence through our curated insights." }} />
                        <div className="pt-4">
                          <span className="editorial-underline font-label font-bold text-sm text-on-surface">Explore Insight</span>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              }

              // Subsequent Rows 스타일
              return (
                <article key={item.id} className="grid md:grid-cols-2 gap-10 items-start group cursor-pointer" onClick={() => onSelectItem(item)}>
                  <div className="order-2 md:order-1 space-y-4">
                    <div className="flex items-center gap-4 text-xs font-label tracking-widest uppercase text-on-surface-variant">
                      <span className="text-primary font-bold">{new Date(item.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      <span className="w-1 h-1 bg-outline-variant rounded-full"></span>
                      <span>Verified Knowledge</span>
                    </div>
                    <h2 className="font-headline text-3xl font-bold leading-tight group-hover:text-primary transition-colors text-on-surface" dangerouslySetInnerHTML={{ __html: item.title.rendered }} />
                    <div className="text-on-surface-variant text-base leading-relaxed line-clamp-2 font-body" dangerouslySetInnerHTML={{ __html: item.excerpt?.rendered || "Leading-edge safety protocols and certification guides for Singapore's workforce..." }} />
                    <div className="pt-2">
                      <span className="editorial-underline font-label font-bold text-sm text-on-surface">Read Full Essay</span>
                    </div>
                  </div>
                  <div className="order-1 md:order-2 overflow-hidden rounded-xl aspect-[4/3] bg-surface-container shadow-sm group-hover:shadow-md transition-all duration-500">
                    <img className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" alt="Technical Article Thumbnail" src={item.smartThumbnail} />
                  </div>
                </article>
              );
            })}
          </div>

          {/* Pagination */}
          <div className="pt-16 flex items-center justify-between border-t border-outline-variant/15">
            <button 
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex items-center gap-2 font-label font-bold text-sm text-on-surface-variant hover:text-primary transition-colors disabled:opacity-20"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
              Previous
            </button>
            <div className="flex items-center gap-6 font-label font-bold text-sm">
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i + 1}
                  onClick={() => handlePageChange(i + 1)}
                  className={`${currentPage === i + 1 ? 'text-primary' : 'text-on-surface-variant hover:text-primary'} transition-colors underline decoration-2 underline-offset-4 decoration-transparent hover:decoration-primary/30`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            <button 
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="flex items-center gap-2 font-label font-bold text-sm text-on-surface-variant hover:text-primary transition-colors disabled:opacity-20"
            >
              Next
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
            </button>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-4 space-y-16">
          {/* Featured Pages Widget */}
          <section className="bg-white p-10 rounded-xl border border-zinc-100 shadow-sm animate-in slide-in-from-right duration-700">
            <h3 className="font-headline text-2xl font-bold mb-8 italic text-on-surface underline decoration-primary/20 decoration-4 underline-offset-8">GreenMark Guides</h3>
            <div className="space-y-6 text-on-surface">
              {pages?.slice(0, 3).map((pg) => (
                <div 
                  key={pg.id} 
                  onClick={() => onSelectItem(pg)}
                  className="group cursor-pointer border-b border-zinc-50 pb-3"
                >
                  <span className="font-body text-sm font-bold text-on-surface-variant group-hover:text-primary transition-colors block" dangerouslySetInnerHTML={{ __html: pg.title.rendered }} />
                  <span className="font-label text-[9px] uppercase tracking-widest opacity-40 mt-1 block">Official Resource</span>
                </div>
              ))}
            </div>
          </section>

          {/* Categories Widget */}
          {/* GreenMark Focus has been removed. */}

          {/* Newsletter Widget has been removed */}

          {/* Connect Widget */}
          <section className="bg-white p-10 rounded-xl border border-zinc-100 shadow-sm text-center">
            <h4 className="font-label text-xs uppercase tracking-widest text-[#0b9949] font-bold mb-6">Connect</h4>
            <div className="flex justify-center gap-6 mb-8 text-[#0b9949]">
              <a href="https://www.facebook.com/profile.php?id=61587506786083" target="_blank" rel="noreferrer" className="hover:scale-110 transition-transform">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://www.linkedin.com/company/greenmark-singapore/?viewAsMember=true" target="_blank" rel="noreferrer" className="hover:scale-110 transition-transform">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
            </div>
            <div className="space-y-4 pt-6 border-t border-zinc-50">
              <p className="text-[10px] font-bold text-on-surface-variant leading-relaxed uppercase tracking-widest animate-pulse">
                Premier Industrial Training & Certification
              </p>
              <p className="text-[8px] opacity-40 uppercase tracking-widest font-label">
                © {new Date().getFullYear()} Greenmark. All Rights Reserved.
              </p>
            </div>
          </section>
        </aside>
      </div>
    </main>
  );
};

export default BlogDashboard;
